import { CardType, CategoryType } from 'types/deck'
import { ManaFontIcons } from 'components/misc/customIcons/ManaFont'

export const zones = [
  'battlefield',
  'hand',
  'library',
  'sideboard',
  'graveyard',
  'exile',
  'commandZone',
  'attractions',
  'junkyard',
  'planes',
  'miscCards',
] as const

export type Zone = (typeof zones)[number]
export type CardSize = 'x-small' | 'small' | 'small-normal' | 'normal' | 'normal-large' | 'large'
export type GroupType = 'diagonal' | 'vertical' | 'horizontal' | 'vertical-adjacent' | 'horizontal-adjacent'
export type AlignType = 'vertical' | 'horizontal'

// [width, height, border-radius, labelFontSize, labelPadding]
export const CardSizes: Record<CardSize, [number, number, number, number, string]> = {
  'x-small': [55, 75, 2, 10, '2px'],
  'small': [98, 138, 5, 10, '4px'],
  'small-normal': [125, 175, 6, 12, '0.5rem 1rem'],
  'normal': [150, 210, 8, 12, '0.5rem 1rem'],
  'normal-large': [175, 245, 10, 14, '0.5rem 1rem'],
  'large': [223, 311, 15, 16, '0.5rem 1rem'],
}

export const FACE_DOWN_ZONES: Zone[] = ['attractions', 'library', 'planes', 'hand']
export const PUBLIC_ZONE: Zone[] = ['battlefield', 'graveyard', 'junkyard', 'exile']
export const HORIZONTAL_DEFUALT_TYPES: string[] = ['Plane', 'Battle', 'Phenomenon']
export const HORIZONTAL_DEFAULT_SUBTYPES: string[] = ['Room']

export type Counter = {
  count: number
  color: string
  primary: boolean // If true, the counter can be effected by keybinds
  icon?: ManaFontIcons
}

export type PlaytesterCardData = {
  id: string
  cardId: string

  notes: string

  token: boolean
  tokenCopy: boolean

  // Stuff that can be overwritten in bulk
  tapped: boolean
  flipped: boolean
  faceDown: boolean // Back of the card sleve
  upsideDown: boolean // 180deg rotation
  revealed: boolean // Top of deck only
  dimmed: boolean

  counters: Record<string, Counter>
  commandTax: number

  customPowerOffset: number
  customToughnessOffset: number
}

export type CleanData = {
  id: string
  cardId: string
}

export type CleanZones = {
  sideboard: CleanData[]
  commandZone: CleanData[]
  library: CleanData[]
  attractions: CleanData[]
  planes: CleanData[]
}

// Any given move within the playtester
export type MoveLog = PlaytesterCardData & {
  // Used on turns where the card has moved
  toZone: Zone | null
  fromZone: Zone | null

  // Sets to current zone when the card is unmoved
  zone: Zone | null
}

export type TurnLog = MoveLog[] // Each index is a move
export type GameLog = TurnLog[] // Each index is a turn

export type PlaytesterV2State = {
  allCards: Record<string, CardType>

  battlefield: PlaytesterCardData[]
  hand: PlaytesterCardData[]
  library: PlaytesterCardData[]
  sideboard: PlaytesterCardData[]
  graveyard: PlaytesterCardData[]
  exile: PlaytesterCardData[]
  commandZone: PlaytesterCardData[]
  attractions: PlaytesterCardData[]
  junkyard: PlaytesterCardData[]
  planes: PlaytesterCardData[]
  miscCards: PlaytesterCardData[]

  cardCoordinates: Record<string, [number, number]>
  categories: Record<string, CategoryType>

  cleanGameZones: CleanZones
  tokens: CleanData[]

  // Multi selected ids
  selected: Record<string, boolean>
  multiSelectingWithoutControl: boolean

  undo: Partial<PlaytesterV2State>[]
  redo: Partial<PlaytesterV2State>[]

  // These two are set by us in the reducer based on the the manual/auto Card/Hand sizes
  cardSize: CardSize
  handSize: CardSize // Size of the card not how many cards in hand lol

  manualCardSize: CardSize
  manualHandSize: CardSize

  autoCardSize: CardSize
  autoHandSize: CardSize

  autoCardSizeEnabled: boolean
  autoHandSizeEnabled: boolean

  cardLabelShown: boolean
  defaultActionOnClick: boolean
  handRotation: boolean
  upkeepReminderActive: boolean
  viewLargeCardOnCursorOver: boolean
  countersWidget: boolean
  gameStatsWidget: boolean
  gameLogOverlay: boolean
  hideFoilOverlay: boolean
  showPopoutHand: boolean
  showOverlaysInPopout: boolean
  disabledFancyZoneHoverEffect: boolean
  disableMuliganMenu: boolean
  disableReloadStop: boolean
  disableOldPlaytesterLink: boolean
  hideFancyZoneArrows: boolean
  fancyZoneScrollDisabled: boolean
  countersOverTextBoxes: boolean

  playmatColor: null | string
  playmatUrl: null | string
  sleevesColor: null | string
  sleevesUrl: null | string
  playmatColorExtendsToLowerThird: boolean

  // Overlays
  peeking: number
  searching: Zone | null
  cardSearch: null | 'cards' | 'tokens'
  allTokensOverlay: boolean
  randomGeneratorOverlay: boolean
  randomGeneratedValues: string[]
  generatingRandomValue: boolean
  openingHandOverlay: boolean
  customCardCreationOverlay: boolean
  stackDeckOverlay: boolean
  searchOverlaySearchValue: string

  // Share shit
  sharePlaytesterOverlay: boolean
  shareUrl: null | string // Only set by the host

  counterModal: boolean
  counterMetaIds: null | string[]
  counterModalFrom: Zone | null

  multiplayerFormat: boolean

  // User counters
  startingLifeTotal: number
  lifeTotal: number
  lifeTotal2: number
  commanderDamangeOne: number
  commanderDamangeTwo: number
  commanderDamangeThree: number
  commanderDamangeFour: number

  turnCounter: number
  infectDamage: number
  experiance: number
  muliganCounter: number

  energy: number
  whiteMana: number
  blueMana: number
  blackMana: number
  redMana: number
  greenMana: number
  colorlessMana: number

  // Global forms and stuff
  xinputField: null | 'Peeking' | 'Draw' | 'Add power' | 'Add toughness' | 'Add +X/+X' | 'X from top' | 'Milling'
  xinputMetaIds: null | string[]
  xinputZone: null | Zone
  xinputValue: number

  // Card details info
  focusedCardMetaId: string | null
  focusedFrom: Zone | null

  // Hovered cards
  currentlyDraggedCards: Record<string, boolean>

  cursorOverCard: string | null
  cursorOverCardZone: Zone | null
  globalCardTransform: string | null

  isCtrlHeldDown: boolean
  maxCardsShownInHand: number

  // Game log info
  gameLog: GameLog
  stackedDeckCardIds: Record<string, boolean>

  sharedWindow: boolean // NOTE - not the popout. Just a shared window
  private: boolean
}
